<script setup lang="ts">
import { nextTick, onMounted, useId, useTemplateRef, watch } from 'vue'

import type { TextareaProps } from './types'

const model = defineModel<string>({ default: '' })

const props = withDefaults(defineProps<TextareaProps>(), {
  rows: 3,
  size: 'md',
  variant: 'outline',
  autoGrow: false,
  disabled: false,
  readonly: false,
  error: false,
  hideLabel: false,
})

const emit = defineEmits<{
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()

const inputId = useId()
const errorId = useId()
const textarea = useTemplateRef<HTMLTextAreaElement>('textarea')
const textareaSizeClasses: Record<NonNullable<TextareaProps['size']>, string> = {
  xs: 'text-xs',
  sm: 'text-xs',
  md: 'text-[0.8125rem]',
  lg: 'text-sm',
  xl: 'text-sm',
}

function resize(): void {
  if (!props.autoGrow || !textarea.value) return
  textarea.value.style.height = 'auto'
  textarea.value.style.height = `${textarea.value.scrollHeight}px`
}

watch(model, () => void nextTick(resize))
watch(
  () => props.autoGrow,
  () => void nextTick(resize),
)
onMounted(resize)
</script>

<template>
  <div
    class="ui-textarea relative w-full font-sans"
    :class="[
      `ui-textarea--${size}`,
      `ui-textarea--${variant}`,
      { 'ui-textarea--error': error, 'ui-textarea--disabled': disabled },
      { 'opacity-60': disabled },
    ]"
  >
    <label
      v-if="label"
      :for="inputId"
      class="ui-textarea__label"
      :class="{ 'ui-textarea__label--sr-only sr-only': hideLabel }"
    >
      {{ label }}
    </label>
    <textarea
      :id="inputId"
      ref="textarea"
      v-model="model"
      class="ui-textarea__field block w-full p-sm text-fg outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted"
      :class="[
        variant === 'plain' ? 'text-lg' : textareaSizeClasses[size],
        variant === 'plain'
          ? 'min-h-11 max-h-48 resize-none rounded-full border-0 bg-transparent shadow-none'
          : 'min-h-[4.5rem] resize-y rounded-sm border',
        variant === 'plain'
          ? ''
          : variant === 'filled'
            ? 'bg-surface-sunken shadow-none'
            : 'bg-surface shadow-input',
        variant === 'plain'
          ? ''
          : error
            ? 'border-danger focus:shadow-[0_0_0_1px_var(--ui-color-danger)]'
            : 'border-border focus:border-ring focus:shadow-[0_0_0_1px_var(--ui-color-ring)]',
        { 'cursor-not-allowed': disabled },
      ]"
      :name="name"
      :rows="rows"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :aria-disabled="disabled"
      :aria-invalid="error || undefined"
      :aria-describedby="error && errorMessage ? errorId : undefined"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />
    <p
      v-if="error && errorMessage"
      :id="errorId"
      class="ui-textarea__error mt-2xs text-[0.6875rem] font-medium text-danger"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
